// @ts-strict-ignore
import parse from 'html-react-parser';
import * as React from 'react';
import { areEqual } from 'react-window';

import { CardTitleV2 } from '@/atoms/CardTitle/CardTitle';
import { Body, BodyImage } from '@/atoms/SourceContent/Body/Body';
import { FeedCard } from '@/components/FeedCard/FeedCard';
import {
  CardSelection,
  CardTitleContainer,
  DocTags,
  SearchNavigator,
} from '@/components/SourceCards/common';
import { CardActionsMenu } from '@/components/SourceCards/components/CardActionsMenu';
import { PrimaryActions } from '@/components/SourceCards/components/PrimaryActions';
import { PublicSourcesSecondaryTitle } from '@/components/SourceCards/components/PublicSourcesSecondaryTitle';
import { useFeedCard } from '@/components/SourceCards/hooks/useFeedCard';
import { useFeedItemTitle } from '@/components/SourceCards/hooks/useFeedItemTitle';
import { useResponsiveConstants } from '@/hooks/responsive/useResponsiveConstants';
import { useDocumentPagination } from '@/hooks/useDocumentPagination';
import { useShowDelete } from '@/hooks/useShowDelete';
import type { FeedCardType } from '@/types';
import { capitalize } from '@/utils/capitalize';
import type { MimeSenderNameType } from '@/utils/getMimeSenderName';
import { mimeSenderName } from '@/utils/getMimeSenderName';
import { getBody } from '@/utils/public-source-utils/getBody';

export const CHAT_SOURCES = ['whatsapp', 'teams', 'telegram', 'slack'];

const UnmemoizedLinkCard: React.ForwardRefExoticComponent<
  FeedCardType & React.RefAttributes<HTMLDivElement>
> = React.forwardRef(({ result, handlers, index }, ref) => {
  const {
    BannerImage,
    linkToSource,
    openSourceClickHandler,
    shareHandler,
    quickRemovalHandler,
    onClickHandler,
    active,
    readStatus,
  } = useFeedCard(result, handlers, index);
  const cardTitle = useFeedItemTitle(result);
  const { mobile } = useResponsiveConstants();

  const { currentParagraph, dispatch } = useDocumentPagination();
  const { shouldShowDelete } = useShowDelete();

  const bodyText = result?.highlights?.length
    ? parse(result.highlights[currentParagraph - 1])
    : getBody(result);

  const previousParagraph = () => dispatch({ type: 'previous_paragraph' });
  const nextParagraph = () => dispatch({ type: 'next_paragraph' });

  return (
    <FeedCard
      ref={ref}
      active={active}
      read={readStatus}
      selected={result.selected}
      onClickHandler={onClickHandler ?? openSourceClickHandler}
      cardAvatar={
        shouldShowDelete && result.category_tab !== 'rss_news' ? (
          <CardSelection
            handlers={handlers}
            index={index}
            selected={result.selected}
          />
        ) : null
      }
      cardTitle={
        <CardTitleContainer
          icon={<BannerImage />}
          title={
            <CardTitleV2
              unix={result.unix}
              read={readStatus}
              title={cardTitle}
              secondaryTitle={getTeritiaryTitle(result)}
            />
          }
        />
      }
      cardPrimaryHeader={
        <>
          <SearchNavigator
            currentPage={currentParagraph}
            onBack={previousParagraph}
            onForward={nextParagraph}
            totalHits={result?.highlights?.length}
          />
          <PrimaryActions
            disableComments={CHAT_SOURCES.includes(result?.source)}
            result={result}
            handleShare={shareHandler}
            handleCommentClickOverRide={onClickHandler}
          />
          <CardActionsMenu
            hideComments={CHAT_SOURCES.includes(result?.source)}
            result={result}
            currentParagraph={currentParagraph}
            handleQuickRemoval={quickRemovalHandler}
            {...(linkToSource && {
              handleOpenInSource: openSourceClickHandler,
            })}
            channelId={result.channel_id}
            handleShare={shareHandler}
            handleCommentClickOverRide={onClickHandler}
          />
        </>
      }
      cardContent={
        <>
          <Body
            text={bodyText}
            attachment={
              typeof result.extracted_image_imgproxy === 'string' && !mobile ? (
                <BodyImage
                  src={result.extracted_image_imgproxy}
                  alt={result.source}
                />
              ) : null
            }
          />
          <DocTags
            source={result.label ? result.label : result.source}
            deeplink={result.deeplink}
          />
        </>
      }
    />
  );
});

export const getTeritiaryTitle = (result) => {
  const isLinks = result.category === 'links';
  if (isLinks) {
    return result?.source &&
      result?.source_details &&
      Object.keys(result?.source_details).length > 0
      ? mimeSenderName({
          source: result.source as MimeSenderNameType['source'],
          sourceDetails: result.source_details,
        })
      : '';
  } else {
    return (
      <div className='flex items-center gap-x-2 truncate'>
        <span className='truncate'>
          {capitalize(result.label ? result.label : result.source)}
        </span>
        <PublicSourcesSecondaryTitle
          sourceId={result.source}
          label={result.label}
        />
      </div>
    );
  }
};

export const LinkCard = React.memo(UnmemoizedLinkCard, areEqual);
